import React from "react";
import { AvatarStory } from "./stories/avatar";
import { BadgeStory } from "./stories/badge";
import { ButtonStory } from "./stories/button";
import { CheckboxStory } from "./stories/checkbox";
import { CheckboxGroupStory } from "./stories/checkbox-group";
import { CloseButtonStory } from "./stories/close-button";
import { CollapsePanelStory } from "./stories/collapse-panel";
import { ColorPickerStory } from "./stories/color-picker";
import { ComponentIconStory } from "./stories/component-icon";
import { CopyButtonStory } from "./stories/copy-button";
import { DatePickerStory } from "./stories/date-picker";
import { DesignTokensStory } from "./stories/design-tokens";
import { DrawerStory } from "./stories/drawer";
import { DrawnCheckStory } from "./stories/drawn-check";
import { DropdownMenuStory } from "./stories/dropdown-menu";
import { EmptyStateStory } from "./stories/empty-state";
import { EntitySwitcherStory } from "./stories/entity-switcher";
import { FileInputStory } from "./stories/file-input";
import { FilterChipsStory } from "./stories/filter-chips";
import { InfoBoxStory } from "./stories/info-box";
import { InputStory } from "./stories/input";
import { LabelStory } from "./stories/label";
import { ListPageStory } from "./stories/list-page";
import { LoaderStory } from "./stories/loader";
import { MentionsStory } from "./stories/mentions";
import { MenuItemStory } from "./stories/menu-item";
import { ModalStory } from "./stories/modal";
import { ModalIconWrapperStory } from "./stories/modal-icon-wrapper";
import { MotionTokensStory } from "./stories/motion-tokens";
import { NestedBadgeStory } from "./stories/nested-badge";
import { NumberStory } from "./stories/number";
import { OnboardingTooltipStory } from "./stories/onboarding-tooltip";
import { OtpInputStory } from "./stories/otp-input";
import { PanelAccordionStory } from "./stories/panel-accordion";
import { PopconfirmStory } from "./stories/popconfirm";
import { PopoverStory } from "./stories/popover";
import { RadioGroupStory } from "./stories/radio-group";
import { RoleBadgeStory } from "./stories/role-badge";
import { SelectboxStory } from "./stories/selectbox";
import { SelectionBarStory } from "./stories/selection-bar";
import { SkeletonStory } from "./stories/skeleton";
import { SliderStory } from "./stories/slider";
import { SpinnerStory } from "./stories/spinner";
import { SplitButtonStory } from "./stories/split-button";
import { StepperStory } from "./stories/stepper";
import { SvgStory } from "./stories/svg";
import { SwitchStory } from "./stories/switch";
import { TableStory } from "./stories/table";
import { TabsStory } from "./stories/tabs";
import { TagStory } from "./stories/tag";
import { TemplateCardStory } from "./stories/template-card";
import { ToastStory } from "./stories/toast";
import { TooltipStory } from "./stories/tooltip";
import { TreeSelectStory } from "./stories/tree-select";
import { TypographyStory } from "./stories/typography";
import { UploadQueueStory } from "./stories/upload-queue";

export type ShowcaseCategory =
  | "foundations"
  | "inputs"
  | "actions"
  | "data"
  | "feedback"
  | "overlay"
  | "layout"
  | "media";

export interface IShowcaseEntry {
  slug: string;
  category: ShowcaseCategory;
  title: string;
  componentName: string;
  sourcePath: string;
  description: string;
  status: "ready" | "todo";
  render: () => JSX.Element;
}

export const CATEGORY_ORDER: ShowcaseCategory[] = [
  "foundations",
  "inputs",
  "actions",
  "data",
  "feedback",
  "overlay",
  "layout",
  "media"
];

export const CATEGORY_LABELS: Record<ShowcaseCategory, string> = {
  foundations: "Foundations",
  inputs: "Inputs",
  actions: "Actions",
  data: "Data Display",
  feedback: "Feedback",
  overlay: "Overlay",
  layout: "Layout",
  media: "Media & Utility"
};

export const DEFAULT_SLUG: string = "button";

export const SLUG_ALIASES: Record<string, string> = {
  buttons: "button",
  badges: "badge",
  inputs: "input",
  "k-modal": "modal"
};

export const SHOWCASE_REGISTRY: IShowcaseEntry[] = [
  {
    slug: "design-tokens",
    category: "foundations",
    title: "Design Tokens",
    componentName: "color-palette.scss · variables.scss · typography.scss · _control.scss",
    sourcePath: "styles/base",
    description:
      "Colour scales, spacing, the radius ladder, the control scale, row density, elevation and stacking order — single source of truth.",
    status: "ready",
    render: (): JSX.Element => React.createElement(DesignTokensStory)
  },
  {
    slug: "motion-tokens",
    category: "foundations",
    title: "Motion Tokens",
    componentName: "motion.scss · KuikaSwap · useSlidingIndicator",
    sourcePath: "styles/base/motion.scss",
    description: "Durations, curves, shared keyframes and the reduced-motion multipliers.",
    status: "ready",
    render: (): JSX.Element => React.createElement(MotionTokensStory)
  },
  {
    slug: "typography",
    category: "foundations",
    title: "Typography",
    componentName: "KuikaH1 / Text / SmallText / …",
    sourcePath: "ui-kit/typography",
    description: "Display, heading, body and caption scales used across the platform.",
    status: "ready",
    render: (): JSX.Element => React.createElement(TypographyStory)
  },
  {
    slug: "input",
    category: "inputs",
    title: "Input",
    componentName: "KuikaInput",
    sourcePath: "ui-kit/kuika-input",
    description: "Single-line, password, number and textarea inputs with affordances and units.",
    status: "ready",
    render: (): JSX.Element => React.createElement(InputStory)
  },
  {
    slug: "otp-input",
    category: "inputs",
    title: "OTP Input",
    componentName: "KuikaOtpInput",
    sourcePath: "ui-kit/kuika-otp-input",
    description: "One-time code field whose digits roll into cells and whose focus ring slides between them.",
    status: "ready",
    render: (): JSX.Element => React.createElement(OtpInputStory)
  },
  {
    slug: "mentions",
    category: "inputs",
    title: "Mentions",
    componentName: "KuikaMentions",
    sourcePath: "ui-kit/kuika-mentions",
    description: "Textarea that offers a list after a prefix (@) and writes the pick into plain text.",
    status: "ready",
    render: (): JSX.Element => React.createElement(MentionsStory)
  },
  {
    slug: "file-input",
    category: "inputs",
    title: "File Input",
    componentName: "KuikaFileInput",
    sourcePath: "ui-kit/kuika-file-input",
    description: "File trigger and drop zone on the control scale; a drop outside accept shakes and says why.",
    status: "ready",
    render: (): JSX.Element => React.createElement(FileInputStory)
  },
  {
    slug: "checkbox",
    category: "inputs",
    title: "Checkbox",
    componentName: "KuikaCheckbox",
    sourcePath: "ui-kit/kuika-checkbox",
    description: "Binary form control with optional label and alignment variants.",
    status: "ready",
    render: (): JSX.Element => React.createElement(CheckboxStory)
  },
  {
    slug: "checkbox-group",
    category: "inputs",
    title: "Checkbox Group",
    componentName: "KuikaCheckboxGroup",
    sourcePath: "ui-kit/kuika-checkbox-group",
    description: "Multi-select group of checkboxes laid out vertically or horizontally.",
    status: "ready",
    render: (): JSX.Element => React.createElement(CheckboxGroupStory)
  },
  {
    slug: "radio-group",
    category: "inputs",
    title: "Radio Group",
    componentName: "KuikaRadioGroup",
    sourcePath: "ui-kit/kuika-radio-group",
    description: "Single-select group with label position and alignment options.",
    status: "ready",
    render: (): JSX.Element => React.createElement(RadioGroupStory)
  },
  {
    slug: "switch",
    category: "inputs",
    title: "Switch",
    componentName: "KuikaSwitch",
    sourcePath: "ui-kit/kuika-switch",
    description: "On/off toggle with optional label and disabled state.",
    status: "ready",
    render: (): JSX.Element => React.createElement(SwitchStory)
  },
  {
    slug: "selectbox",
    category: "inputs",
    title: "Selectbox",
    componentName: "KuikaSelectbox",
    sourcePath: "ui-kit/kuika-selectbox",
    description: "Single, multi-select, combobox and tags variants over rc-select.",
    status: "ready",
    render: (): JSX.Element => React.createElement(SelectboxStory)
  },
  {
    slug: "tree-select",
    category: "inputs",
    title: "Tree Select",
    componentName: "KuikaTreeSelect",
    sourcePath: "ui-kit/kuika-tree-select",
    description: "Hierarchical multi-select with tag chips, and a searchable single-pick floating mode.",
    status: "ready",
    render: (): JSX.Element => React.createElement(TreeSelectStory)
  },
  {
    slug: "slider",
    category: "inputs",
    title: "Slider",
    componentName: "KuikaSlider",
    sourcePath: "ui-kit/kuika-slider",
    description: "Numeric range control with size, unit, precision and inline input.",
    status: "ready",
    render: (): JSX.Element => React.createElement(SliderStory)
  },
  {
    slug: "color-picker",
    category: "inputs",
    title: "Color Picker",
    componentName: "KuikaColorPicker",
    sourcePath: "ui-kit/kuika-color-picker",
    description: "Sketch-style color picker with preset swatches and positionable popover.",
    status: "ready",
    render: (): JSX.Element => React.createElement(ColorPickerStory)
  },
  {
    slug: "date-picker",
    category: "inputs",
    title: "Date Picker",
    componentName: "KuikaDatePicker",
    sourcePath: "ui-kit/kuika-date-picker",
    description:
      "Date and date-time picker over a popover calendar; paging a month slides the day grid in the paging direction.",
    status: "ready",
    render: (): JSX.Element => React.createElement(DatePickerStory)
  },
  {
    slug: "entity-switcher",
    category: "inputs",
    title: "Entity Switcher",
    componentName: "KuikaEntitySwitcher · KuikaEntityPicker",
    sourcePath: "ui-kit/kuika-entity-switcher",
    description: "The editor bars' record switcher: search, folders and one sliding marker on the open record.",
    status: "ready",
    render: (): JSX.Element => React.createElement(EntitySwitcherStory)
  },
  {
    slug: "filter-chips",
    category: "inputs",
    title: "Filter Chips",
    componentName: "KuikaFilterChips",
    sourcePath: "ui-kit/kuika-filter-chips",
    description: "Toggleable 28px filter pills; a selected chip draws a check and its count rolls when it changes.",
    status: "ready",
    render: (): JSX.Element => React.createElement(FilterChipsStory)
  },
  {
    slug: "button",
    category: "actions",
    title: "Button",
    componentName: "KuikaButton",
    sourcePath: "ui-kit/kuika-button",
    description: "Action trigger with type, size, color, loading and icon affordances.",
    status: "ready",
    render: (): JSX.Element => React.createElement(ButtonStory)
  },
  {
    slug: "split-button",
    category: "actions",
    title: "Split Button",
    componentName: "KuikaSplitButton",
    sourcePath: "ui-kit/kuika-split-button",
    description:
      "A main action joined to a chevron menu of its alternatives, pressed and given a status as one control.",
    status: "ready",
    render: (): JSX.Element => React.createElement(SplitButtonStory)
  },
  {
    slug: "copy-button",
    category: "actions",
    title: "Copy Button",
    componentName: "KuikaCopyButton",
    sourcePath: "ui-kit/kuika-copy-button",
    description: "Copy-to-clipboard control whose glyph rolls to a check on success and back after 1.5 seconds.",
    status: "ready",
    render: (): JSX.Element => React.createElement(CopyButtonStory)
  },
  {
    slug: "dropdown-menu",
    category: "actions",
    title: "Dropdown Menu",
    componentName: "KuikaDropdownMenu",
    sourcePath: "ui-kit/kuika-dropdown-menu",
    description: "Menu surface with nested groups, dividers, badges and click or hover triggers.",
    status: "ready",
    render: (): JSX.Element => React.createElement(DropdownMenuStory)
  },
  {
    slug: "selection-bar",
    category: "actions",
    title: "Selection Bar",
    componentName: "KuikaSelectionBar",
    sourcePath: "ui-kit/kuika-selection-bar",
    description: "Floating bulk-action bar that rises in with a rolling count and clears on Escape.",
    status: "ready",
    render: (): JSX.Element => React.createElement(SelectionBarStory)
  },
  {
    slug: "badge",
    category: "data",
    title: "Badge",
    componentName: "KuikaBadge",
    sourcePath: "ui-kit/kuika-badge",
    description: "Status and label chips with eight color tokens and three sizes.",
    status: "ready",
    render: (): JSX.Element => React.createElement(BadgeStory)
  },
  {
    slug: "nested-badge",
    category: "data",
    title: "Nested Badge",
    componentName: "KuikaNestedBadge",
    sourcePath: "ui-kit/kuika-nested-badge",
    description: "Parent badge with grouped children — used for tagged filter chips.",
    status: "ready",
    render: (): JSX.Element => React.createElement(NestedBadgeStory)
  },
  {
    slug: "role-badge",
    category: "data",
    title: "Role Badge",
    componentName: "KuikaRoleBadge",
    sourcePath: "ui-kit/kuika-role-badge",
    description: "A user's workspace or project role, in that role's fixed colour.",
    status: "ready",
    render: (): JSX.Element => React.createElement(RoleBadgeStory)
  },
  {
    slug: "tag",
    category: "data",
    title: "Tag",
    componentName: "KuikaTag",
    sourcePath: "ui-kit/kuika-tag",
    description: "Hashtag-style inline label with action / default variants.",
    status: "ready",
    render: (): JSX.Element => React.createElement(TagStory)
  },
  {
    slug: "label",
    category: "data",
    title: "Label",
    componentName: "KuikaLabel",
    sourcePath: "ui-kit/kuika-label",
    description: "Static color-coded label with optional left/right icons.",
    status: "ready",
    render: (): JSX.Element => React.createElement(LabelStory)
  },
  {
    slug: "table",
    category: "data",
    title: "Table",
    componentName: "KuikaTable",
    sourcePath: "ui-kit/kuika-table",
    description: "TanStack-based data table — sorting, filters, visibility, selection and expand.",
    status: "ready",
    render: (): JSX.Element => React.createElement(TableStory)
  },
  {
    slug: "number",
    category: "data",
    title: "Number",
    componentName: "KuikaNumber",
    sourcePath: "ui-kit/kuika-number",
    description: "A live number that rolls only the digits that change, up when it grows and down when it shrinks.",
    status: "ready",
    render: (): JSX.Element => React.createElement(NumberStory)
  },
  {
    slug: "template-card",
    category: "data",
    title: "Template Card",
    componentName: "KuikaTemplateCard",
    sourcePath: "ui-kit/kuika-template-card",
    description: "Gallery card with cover artwork, name, description and actions rising over the artwork on hover.",
    status: "ready",
    render: (): JSX.Element => React.createElement(TemplateCardStory)
  },
  {
    slug: "info-box",
    category: "feedback",
    title: "Info Box",
    componentName: "KuikaInfoBox",
    sourcePath: "ui-kit/kuika-info-box",
    description: "Static info banner with five color tones and slot-based content.",
    status: "ready",
    render: (): JSX.Element => React.createElement(InfoBoxStory)
  },
  {
    slug: "empty-state",
    category: "feedback",
    title: "Empty State",
    componentName: "KuikaEmptyState",
    sourcePath: "ui-kit/kuika-empty-state",
    description: "Illustrated empty placeholder for lists with primary action.",
    status: "ready",
    render: (): JSX.Element => React.createElement(EmptyStateStory)
  },
  {
    slug: "toast",
    category: "feedback",
    title: "Toast",
    componentName: "KuikaToast",
    sourcePath: "ui-kit/kuika-toast",
    description: "Inline status notification with success, info, warning and error tones.",
    status: "ready",
    render: (): JSX.Element => React.createElement(ToastStory)
  },
  {
    slug: "upload-queue",
    category: "feedback",
    title: "Upload Queue",
    componentName: "KuikaUploadQueue / KuikaUploadItem",
    sourcePath: "ui-kit/kuika-upload-queue",
    description: "Upload rows that arrive, show progress, draw a check, shake on failure and collapse away.",
    status: "ready",
    render: (): JSX.Element => React.createElement(UploadQueueStory)
  },
  {
    slug: "modal",
    category: "overlay",
    title: "Modal",
    componentName: "KModal",
    sourcePath: "ui-kit/k-modal",
    description: "rc-dialog backed modal. Optional header, free-form footer slot, body via children.",
    status: "ready",
    render: (): JSX.Element => React.createElement(ModalStory)
  },
  {
    slug: "drawer",
    category: "overlay",
    title: "Drawer",
    componentName: "KuikaDrawer",
    sourcePath: "ui-kit/kuika-drawer",
    description:
      "Radix Dialog side panel with a long eased entrance, blurred backdrop, short exit and four placements.",
    status: "ready",
    render: (): JSX.Element => React.createElement(DrawerStory)
  },
  {
    slug: "close-button",
    category: "overlay",
    title: "Close Button",
    componentName: "KuikaCloseButton",
    sourcePath: "ui-kit/kuika-close-button",
    description:
      "The one dismiss control for modals, drawers, popovers and toasts, with a 32px target and a focus ring.",
    status: "ready",
    render: (): JSX.Element => React.createElement(CloseButtonStory)
  },
  {
    slug: "modal-icon-wrapper",
    category: "overlay",
    title: "Modal Icon Wrapper",
    componentName: "KuikaModalIconWrapper",
    sourcePath: "ui-kit/kuika-modal-icon-wrapper",
    description: "One-tone glow icon badge — used inside KModal headers.",
    status: "ready",
    render: (): JSX.Element => React.createElement(ModalIconWrapperStory)
  },
  {
    slug: "popconfirm",
    category: "overlay",
    title: "Popconfirm",
    componentName: "KuikaPopconfirm",
    sourcePath: "ui-kit/kuika-popconfirm",
    description:
      "The yes/no asked where the action is: a KuikaPopover surface with the alertdialog role, a danger tone, and Escape or a press outside answering cancel.",
    status: "ready",
    render: (): JSX.Element => React.createElement(PopconfirmStory)
  },
  {
    slug: "popover",
    category: "overlay",
    title: "Popover",
    componentName: "KuikaPopover",
    sourcePath: "ui-kit/kuika-popover",
    description: "Anchored content surface with four positions and optional title bar.",
    status: "ready",
    render: (): JSX.Element => React.createElement(PopoverStory)
  },
  {
    slug: "tooltip",
    category: "overlay",
    title: "Tooltip",
    componentName: "KuikaTooltip",
    sourcePath: "ui-kit/kuika-tooltip",
    description: "Radix-backed tooltip with position, alignment and color tokens.",
    status: "ready",
    render: (): JSX.Element => React.createElement(TooltipStory)
  },
  {
    slug: "onboarding-tooltip",
    category: "overlay",
    title: "Onboarding Tooltip",
    componentName: "KuikaOnboardingTooltip",
    sourcePath: "ui-kit/kuika-onboarding-tooltip",
    description: "Coach-marks with multi-step content, icons and learn-more links.",
    status: "ready",
    render: (): JSX.Element => React.createElement(OnboardingTooltipStory)
  },
  {
    slug: "collapse-panel",
    category: "layout",
    title: "Collapse Panel",
    componentName: "KuikaCollapsePanel",
    sourcePath: "ui-kit/kuika-collapse-panel",
    description: "Accordion list with left/right chevron, single or multi-expand and animation.",
    status: "ready",
    render: (): JSX.Element => React.createElement(CollapsePanelStory)
  },
  {
    slug: "tabs",
    category: "layout",
    title: "Tabs",
    componentName: "KuikaTabs",
    sourcePath: "ui-kit/kuika-tabs",
    description: "Segmented tab control with two sizes and string or rich option entries.",
    status: "ready",
    render: (): JSX.Element => React.createElement(TabsStory)
  },
  {
    slug: "panel-accordion",
    category: "layout",
    title: "Panel Accordion",
    componentName: "KuikaPanelAccordion",
    sourcePath: "ui-kit/kuika-panel-accordion",
    description: "Single titled section with chevron, used in side panels.",
    status: "ready",
    render: (): JSX.Element => React.createElement(PanelAccordionStory)
  },
  {
    slug: "stepper",
    category: "layout",
    title: "Stepper",
    componentName: "KuikaStepper",
    sourcePath: "ui-kit/kuika-stepper",
    description: "Linear wizard progress that fills in order moving forward and lands at once moving back.",
    status: "ready",
    render: (): JSX.Element => React.createElement(StepperStory)
  },
  {
    slug: "list-page",
    category: "layout",
    title: "List Page",
    componentName: "KuikaListPage",
    sourcePath: "ui-kit/kuika-list-page",
    description: "Landing-page scaffold: header, toolbar, create cards, table or grid, and the empty state.",
    status: "ready",
    render: (): JSX.Element => React.createElement(ListPageStory)
  },
  {
    slug: "svg",
    category: "media",
    title: "SVG",
    componentName: "KuikaSvg",
    sourcePath: "ui-kit/kuika-svg",
    description: "Inline SVG renderer across thirteen icon namespaces with color override.",
    status: "ready",
    render: (): JSX.Element => React.createElement(SvgStory)
  },
  {
    slug: "component-icon",
    category: "media",
    title: "Component Icon",
    componentName: "KuikaComponentIcon",
    sourcePath: "ui-kit/kuika-component-icon",
    description: "Mask-based component thumbnails with hover swap state.",
    status: "ready",
    render: (): JSX.Element => React.createElement(ComponentIconStory)
  },
  {
    slug: "avatar",
    category: "media",
    title: "Avatar",
    componentName: "KuikaAvatar",
    sourcePath: "ui-kit/kuika-avatar",
    description: "A person's circle: photo or initials in one of ten identity colours keyed off a seed.",
    status: "ready",
    render: (): JSX.Element => React.createElement(AvatarStory)
  },
  {
    slug: "spinner",
    category: "media",
    title: "Spinner",
    componentName: "KuikaSpinner / KuikaSpinnerSmall / KuikaSmallSpinner",
    sourcePath: "ui-kit/kuika-spinner",
    description:
      "Kuika logo spinners: the page overlay and the in-region KuikaSpinnerSmall. KuikaSmallSpinner is the old name of KuikaLoader's ring.",
    status: "ready",
    render: (): JSX.Element => React.createElement(SpinnerStory)
  },
  {
    slug: "loader",
    category: "media",
    title: "Loader",
    componentName: "KuikaLoader",
    sourcePath: "ui-kit/kuika-loader",
    description: "Ring, dots and logo loading indicators on the control scale; they breathe under reduced motion.",
    status: "ready",
    render: (): JSX.Element => React.createElement(LoaderStory)
  },
  {
    slug: "skeleton",
    category: "media",
    title: "Skeleton",
    componentName: "KuikaSkeleton / KuikaSkeletonRegion",
    sourcePath: "ui-kit/kuika-skeleton",
    description: "One skeleton language: shape compositions, a region that announces loading, and the show-delay gate.",
    status: "ready",
    render: (): JSX.Element => React.createElement(SkeletonStory)
  },
  {
    slug: "drawn-check",
    category: "media",
    title: "Drawn Check",
    componentName: "KuikaDrawnCheck",
    sourcePath: "ui-kit/kuika-drawn-check",
    description: "The stroked check every surface that marks something done draws, and the kit's one inline SVG.",
    status: "ready",
    render: (): JSX.Element => React.createElement(DrawnCheckStory)
  },
  {
    slug: "menu-item",
    category: "media",
    title: "Menu Item",
    componentName: "KuikaMenuItem",
    sourcePath: "ui-kit/kuika-menu-item",
    description: "Selectable row with icon, trailing units and selected state.",
    status: "ready",
    render: (): JSX.Element => React.createElement(MenuItemStory)
  }
];
